import { useCallback, useState } from "react";
import { shallowEqual } from "react-redux";

// Spark components
import {
    GuxButton,
    GuxIcon,
    GuxInlineAlert,
    GuxTooltip,
} from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";

// Common
import { DefaultLimits } from "constants/DefaultLimits";
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import AutomatedScoringType from "enums/AutomatedScoringType";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";

// Feature slice
import {
    qualityFormActions,
    selectAutomatedScoringOption,
} from "features/qualityForm/qualityFormSlice";
import {
    selectQuestion,
    selectMaxAiScoringAnswerOptionCount,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Validations
import {
    hasMaximumAIScoringAnswerOptions,
    selectIsQuestionValidForUserDefinedDefaultAnswer,
} from "features/qualityForm/qualityFormValidations";
import {
    hasMinimumAnswerOptions,
    hasMaximumAnswerOptions,
} from "features/qualityForm/qualityFormValidations/qualityFormAnswerValidations";

// Question common
import { PropertySection } from "../common/PropertySection";
import { AnswerOption } from "./AnswerOptionInput";

// Strings
import strings from "./Question.strings";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";

export default function AnswerOptionsSection({
    questionId,
}: {
    questionId: string;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);

    const answerOptions = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return question?.answerOptions;
    }, shallowEqual);

    // Move these selectors from QuestionEditor into this component
    const hasEnoughAnswerOptions = useAppSelector((state) =>
        hasMinimumAnswerOptions(state, questionId)
    );

    const hasTooManyAnswerOptions = useAppSelector((state) =>
        hasMaximumAnswerOptions(state, questionId)
    );

    const hasTooManyAIScoringAnswerOptions = useAppSelector((state) => {
        return (
            selectAutomatedScoringOption(state, questionId) ===
                AutomatedScoringType.AI_SCORING &&
            hasMaximumAIScoringAnswerOptions(state, questionId)
        );
    });

    const { isCritical, maxAIScoringAnswerOptionCount, isWem1532FTEnabled } =
        useAppSelector((state) => {
            const question = selectQuestion(state, questionId);
            return {
                isCritical: question?.isCritical,
                maxAIScoringAnswerOptionCount:
                    selectMaxAiScoringAnswerOptionCount(state),
                isWem1532FTEnabled: selectFeatureToggle(
                    state,
                    FeatureToggle.AI_SCORING_LIMIT_INCREASE_FOR_ANSWER_OPTIONS
                ),
            };
        }, shallowEqual);

    const disabled =
        isCritical ||
        hasTooManyAnswerOptions ||
        hasTooManyAIScoringAnswerOptions;

    const showAddAnswerTooltip =
        disabled &&
        (isCritical ||
            hasTooManyAnswerOptions ||
            (!isWem1532FTEnabled && hasTooManyAIScoringAnswerOptions));

    const [shouldFocusNewOption, setShouldFocusNewOption] = useState(false);

    const onAutoFocused = useCallback(() => {
        setShouldFocusNewOption(false);
    }, []);

    const showDefaultAnswerRequiredError = !useAppSelector((state) =>
        selectIsQuestionValidForUserDefinedDefaultAnswer(state, questionId)
    );

    return (
        <PropertySection title={t.labels.answerOptions}>
            {answerOptions?.map((value, index) => {
                const isLast = index === (answerOptions?.length ?? 0) - 1;
                return (
                    <AnswerOption
                        answerOptionId={value}
                        questionId={questionId}
                        canDeleteAnswer={hasEnoughAnswerOptions}
                        answerIndex={index}
                        key={value}
                        shouldAutoFocus={isLast && shouldFocusNewOption}
                        onAutoFocused={onAutoFocused}
                    />
                );
            })}
            {showDefaultAnswerRequiredError && (
                <span data-testid="default-answer-required">
                    <ErrorStatement
                        errorMessage={
                            t.errors.defaultAnswerRequiredForUserDefined
                        }
                    />
                </span>
            )}
            <div
                className="add-answer-button-group"
                data-testid="add-answer-button-group"
            >
                <GuxButton
                    className="add-answer"
                    data-testid="add-answer-button"
                    disabled={toGuxDisabled(disabled)}
                    aria-disabled={toGuxDisabled(disabled)}
                    onClick={() => {
                        dispatch(
                            qualityFormActions.addAnswerOption({
                                questionId: questionId,
                            })
                        );
                        setShouldFocusNewOption(true);
                    }}
                >
                    <GuxIcon icon-name="add" decorative />
                    <span
                        className={`add-answer-label${
                            disabled ? "-disabled" : ""
                        }`}
                    >
                        {t.labels.addAnswer}
                    </span>
                </GuxButton>
                {showAddAnswerTooltip && (
                    <GuxTooltip>
                        <div slot="content">
                            {isCritical &&
                                t.labels.criticalQuestionAnswerLimitReached({
                                    criticalAnswerOptionCount:
                                        DefaultLimits.maxCriticalAnswerOptionCount,
                                })}
                            {hasTooManyAnswerOptions &&
                                t.labels.answerLimitReached({
                                    maxAnswerOptionCount:
                                        DefaultLimits.maxAnswerOptionCount,
                                })}
                            {!isWem1532FTEnabled &&
                                hasTooManyAIScoringAnswerOptions &&
                                t.labels.aiScoringAnswerLimitReached({
                                    maxAIScoringAnswerOptionCount,
                                })}
                        </div>
                    </GuxTooltip>
                )}
            </div>
            {isWem1532FTEnabled && hasTooManyAIScoringAnswerOptions && (
                <GuxInlineAlert
                    accent="info"
                    className="AiScoringAnswerLimitBanner"
                >
                    <div
                        slot="content"
                        role="alert"
                        data-testid="ai-scoring-answer-limit-info-banner-answer-section"
                    >
                        {t.labels.aiScoringAnswerLimitReached({
                            maxAIScoringAnswerOptionCount,
                        })}
                    </div>
                </GuxInlineAlert>
            )}
        </PropertySection>
    );
}
